import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmbeddedIvyChat } from '@/components/agent-chat/EmbeddedIvyChat';
import { LivingDots } from '@/components/studio/LivingDots';
import { ivyStateOf } from '@/lib/ivyChat/ivyNow';
import type { Takeover } from '@/components/agent-chat/IvyTakeoverB';
import type { Slide } from '@/stores/videoWorkspaceStore';
import { IvyContextSwitcher } from './IvyContextSwitcher';
import { SIDE_WIDTH } from '@/lib/studioB/useRoomForBothSides';
import { bMove } from '@/lib/studioB/motion';

type IvyChatProps = React.ComponentProps<typeof EmbeddedIvyChat>;

interface StudioIvyColumnProps {
  open: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
  conversationId: string;
  slides: Slide[];
  conversationTitle: string;
  systemContext: IvyChatProps['systemContext'];
  estimateSku: IvyChatProps['estimateSku'];
  /** Direction B (beta only): Ivy starts centred over the row and moves to the rail, one chat throughout. */
  b?: { centred: boolean; phone?: boolean; autoSendMessage?: string; takeover?: Takeover };
}

/** Ivy on the left of the studio: its header, then the chat. Slides shut to zero width when closed. */
export const StudioIvyColumn: React.FC<StudioIvyColumnProps> = (props) => {
  if (props.b) return <IvyColumnB {...props} b={props.b} />;
  const { open, expanded, conversationId, conversationTitle, systemContext, estimateSku } = props;
  return (
    <motion.div
      className={cn(
        "flex-shrink-0 flex flex-col studio-panel-elevated overflow-hidden"
      )}
      initial={false}
      animate={{
        width: open ? (expanded ? '50%' : 340) : 0,
        opacity: open ? 1 : 0,
      }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className={cn(
        "h-full flex flex-col",
        expanded ? "w-full" : "w-[340px]",
        "transition-all duration-300 ease-out",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <>
          <IvyColumnHeader {...props} />
          {/* Ivy Chat - Claude Code powered */}
          <div className="flex-1 overflow-hidden">
            <EmbeddedIvyChat
              conversationId={conversationId}
              conversationTitle={conversationTitle}
              systemContext={systemContext}
              estimateSku={estimateSku}
            />
          </div>
        </>
      </div>
    </motion.div>
  );
};

function IvyColumnHeader({ expanded, onToggleExpanded, onClose, conversationId, slides, b, canExpand = true }: StudioIvyColumnProps & { canExpand?: boolean }) {
  return (
    <div className="h-10 flex items-center justify-between px-3 flex-shrink-0 border-b border-border/60">
      {b ? (
        <span data-rail-name className="pl-1 text-b-label text-b-t1">Ivy</span>
      ) : (
        <IvyContextSwitcher
          parentConversationId={conversationId}
          slides={slides}
          onContextPrefixChange={() => {}}
        />
      )}
      <div className="flex items-center gap-0.5">
        {canExpand && <button
          onClick={onToggleExpanded}
          className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          title={expanded ? 'Compact view' : 'Expand chat'}
        >
          {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>}
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/** Whether Ivy is working in this video, as the chat publishes it (ivy-state). */
function useIvyWorking(conversationId: string): boolean {
  const [state, setState] = useState(() => ivyStateOf(conversationId));
  useEffect(() => {
    setState(ivyStateOf(conversationId));
    const onState = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.conversationId === conversationId) setState(detail.state);
    };
    window.addEventListener('ivy-state', onState);
    return () => window.removeEventListener('ivy-state', onState);
  }, [conversationId]);
  return state !== 'idle';
}

const RAIL = SIDE_WIDTH;
/** Expanded, Ivy widens in place on the left to this, leaving the stage at least STAGE_MIN; she only widens where that
 * gives her WIDEN_BY more than the rail (not on an upright iPad). */
const IVY_WIDE = 640;
const STAGE_MIN = 480;
const WIDEN_BY = 80;
const wideFor = () => Math.min(IVY_WIDE, window.innerWidth - STAGE_MIN);

function useWideWidth(enabled: boolean): number {
  const [width, setWidth] = useState(wideFor);
  useEffect(() => {
    if (!enabled) return;
    const onResize = () => setWidth(wideFor());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled]);
  return width;
}
/** On a phone, how much of Ivy's sheet shows over the studio: the end of the thread and the composer. */
export const SHEET_PEEK = 232;
const SWIPE = 24;

/**
 * A slot keeps the rail's place in the row, so the stage beside it is laid out at its final size even while Ivy is
 * over it. The panel holding the one chat lies over the row: centred it spans it on the ground, with the conversation
 * in the reading column; docked it is the slot's width, sliding in from the left as the slot opens; expanded it widens
 * in place and the stage moves over (Rahul, 31 Oct: wider on the left, not back to the centre).
 */
function IvyColumnB(props: StudioIvyColumnProps & { b: NonNullable<StudioIvyColumnProps['b']> }) {
  const { open, expanded, conversationId, conversationTitle, systemContext, estimateSku, b } = props;
  const sheet = !!b.phone && !b.centred;
  const shown = b.centred || open || sheet;
  const wideAt = useWideWidth(!b.phone);
  const canWiden = !b.phone && !b.centred && wideAt >= RAIL + WIDEN_BY;
  const wide = canWiden && open && expanded;
  const width = wide ? wideAt : RAIL;
  const place = b.centred ? 'centre' : sheet ? 'sheet' : 'rail';
  const [full, setFull] = useState(false);
  const press = useRef<{ y: number; swiped: boolean } | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const working = useIvyWorking(conversationId);
  // F3: centre stage sits on the living dot field; its rings start at the line saying what Ivy is doing.
  const workingAt = () => {
    const lines = panel.current?.querySelectorAll('[role="status"]');
    const line = lines?.[lines.length - 1];
    if (!line) return null;
    const box = line.getBoundingClientRect();
    return { x: box.left, y: box.top + box.height / 2 };
  };

  return (
    <>
      <motion.div aria-hidden className="flex-shrink-0" initial={false} animate={{ width: shown && !b.phone ? width : 0 }} transition={bMove()} />
      <div
        ref={panel}
        data-ivy={place}
        data-wide={wide ? '' : undefined}
        data-sheet={sheet ? (full ? 'full' : 'peek') : undefined}
        inert={!shown}
        className={cn(
          b.phone ? 'absolute inset-x-0 bottom-0 z-20 flex flex-col overflow-hidden' : 'absolute inset-y-0 left-0 z-20 flex flex-col overflow-hidden',
          b.phone
            ? 'transition-[height,background-color] duration-b-move ease-b motion-reduce:transition-none'
            : 'transition-[width,background-color,opacity,transform] duration-b-move ease-b motion-reduce:transition-none',
          place === 'centre' && 'bg-b-ground',
          place === 'rail' && 'border-r border-b-line bg-b-chrome',
          place === 'sheet' && 'rounded-t-[var(--b-radius-sheet)] border-t border-b-line bg-b-panel shadow-[0_-10px_30px_-12px_rgb(0_0_0/0.3)]',
          !shown && 'pointer-events-none opacity-0',
          !shown && !b.phone && '-translate-x-[calc(100%+12px)]',
        )}
        style={b.phone
          ? { height: b.centred ? '100%' : full ? '88%' : `${SHEET_PEEK}px` }
          : { width: !shown ? '0px' : place === 'rail' ? `${width}px` : '100%', '--b-ivy-w': `${width}px` } as React.CSSProperties}
      >
        {place === 'centre' && <LivingDots working={working} column={760} origin={workingAt} />}
        {sheet && (
          <button
            type="button"
            aria-label={full ? 'Show less' : 'Show the whole conversation'}
            aria-expanded={full}
            className="flex h-7 w-full flex-shrink-0 touch-none items-center justify-center"
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture?.(e.pointerId);
              press.current = { y: e.clientY, swiped: false };
            }}
            onPointerUp={(e) => {
              const dy = press.current ? e.clientY - press.current.y : 0;
              if (press.current && Math.abs(dy) > SWIPE) {
                press.current.swiped = true;
                setFull(dy < 0);
              }
            }}
            onClick={() => {
              const swiped = press.current?.swiped;
              press.current = null;
              if (!swiped) setFull((f) => !f);
            }}
          >
            <span aria-hidden className="h-1 w-9 rounded-full bg-b-line-strong" />
          </button>
        )}
        <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-hidden">
          {!b.centred && !b.phone && <IvyColumnHeader {...props} canExpand={canWiden} />}
          <EmbeddedIvyChat
            conversationId={conversationId}
            conversationTitle={conversationTitle}
            systemContext={systemContext}
            estimateSku={estimateSku}
            autoSendMessage={b.autoSendMessage}
            takeover={b.takeover}
            docked={!b.centred}
            peek={sheet && !full}
          />
        </div>
      </div>
    </>
  );
}
